import React, { useState, useEffect, useMemo } from "react"
import { get, take, takeRight, chunk, isNull, isEmpty } from "lodash"

import { Panel } from "components/organisms/panel"
import { BaseTable, EmptyTable } from "components/molecules/table"
import { SpDetailTable } from "components/molecules/table/spTable"
import { tableColumns } from "config/table_columns"
import { Sort } from "utils/sort"
import { RankingSummaryPanel } from "components/organisms/panel/rankingSummary"
import { MainIndexList } from "components/organisms/panel/mainIndexList"
import { SummaryChart } from "components/organisms/panel/chart"
import { DomesticSummaryIPO } from "components/organisms/panel/data"
import { IPODataType } from "components/types/pages/domestic"
import { NewsTopicsPanel } from "components/organisms/panel/newsTopics"
import { APIResponse } from "redux/apis/types"
import APIs from "redux/apis"
import { Pagination } from "components/molecules/pagination"
import { StatisticList } from "components/molecules/list/statistics"
import * as Formatter from "utils/Formatter"
import { PAGE_NUM, ROUTE_LIST, SECURITY_TYPE, history } from "config/const"
import { NewsList } from "components/organisms/panel/newsList"
import { MainContainer } from "components/molecules/utils/mainContainer"
import { checkIsSp } from "utils/util"
import { BaseSelect } from "components/atoms/input/select"
import { statisticsApiCall } from "components/actions/divisionStatistics"
import dayjs from "dayjs"

type RankingDataType = {
  exchangeTypeNum: number
  priceIncreaseList: Array<object>
  priceReductionList: Array<object>
  volumeList: Array<object>
  priceIncreaseTime: string
  priceReductionTime: string
  volumeTime: string
}

type indicatorItem = {
  data: Array<APIResponse.Res>
  changeData: Function
  selectDay: number
  changeDay: Function
  currentPage: number
  changePage: Function
}

type Props = {
  indexData: Array<object>
  setIndexData: Function
  statisticsData: APIResponse.Res
  updateStatistics: Function
  rankingData: RankingDataType
  changeRanking: Function
  callRankingApi: Function
  ipoData: IPODataType
  setIpoData: Function
  industryData: APIResponse.Res
  setIndustryData: Function
  indicatorItem: indicatorItem
  history: history
  requestIndex: Function
  requestIpo: Function
  requestIndicatorList: Function
}

export const DomesticSummaryTemplate = (props: Props) => {
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])

  const chunkIndicatorData: Array<Array<object>> = chunk(
    props.indicatorItem.data,
    PAGE_NUM.PAGE_10
  )

  const getIndsutryData = () => {
    //業種別騰落率、クリア電文後は騰落率がnullでくるためランキングを表示しない
    if (isNull(get(props, "industryData.ResultList.0.ChangedRatio"))) {
      return []
    }
    return get(props, "industryData.ResultList", [])
  }
  const industryData: Array<object> | [] = getIndsutryData()

  const industryPriceIncreaseRate = take(industryData, 5)
  const industryPriceDropRate = takeRight(industryData, 5).reverse()

  return (
    <MainContainer className="symmetry-layout domestic-summary-template">
      <div className="contents-block">
        <div className="contents-nest-block">
          <SummaryChart dataList={props.indexData} />
          <MainIndexList
            addClass="domestic-summary"
            indexData={props.indexData}
            scrollable={false}
            reloadList={() => {
              props.requestIndex({
                pathName: "Jpn.Summary.Main",
                setApiRes: props.setIndexData,
              })
            }}
            onClick={(rowData: object) => {
              if (get(rowData, "SecurityType") === SECURITY_TYPE.JPGBY10.id) {
                props.history.push({
                  pathname: "/top/bonds",
                  state: { rowData },
                })
              } else if (
                get(rowData, "SecurityType") === SECURITY_TYPE.PIR.id
              ) {
                props.history.push("/top/bank_rate")
              } else {
                props.history.push({
                  pathname: "/domestic/index",
                  state: { rowData },
                })
              }
            }}
            domesticSummaryFlag={true}
          />
          <Panel
            title={
              <>
                <span className="panel-header-title-items">市場統計</span>
                {/*<span className="panel-header-title-items">*/}
                {/*  <div className="time-col liquid-item">*/}
                {/*    {Formatter.date(*/}
                {/*      get(props.statisticsData[0], "CreatedTime"),*/}
                {/*      "MM/DD HH:mm"*/}
                {/*    )}*/}
                {/*  </div>*/}
                {/*</span>*/}
              </>
            }
            // refresh={() => {
            //   statisticsApiCall({
            //     updateStatistics: props.updateStatistics,
            //     division: "0111,0114,0124",
            //   })
            // }}
            className="panel-market-statistics"
          >
            <StatisticList
              statisticsData={[
                props.statisticsData[0],
                props.statisticsData[1],
                props.statisticsData[2],
              ]}
            />
          </Panel>
          <RankingSummaryPanel
            title="株式ランキング"
            rankingData={props.rankingData}
            exchangeList={Sort.DomesticSummaryRankingExchange}
            changeRanking={props.changeRanking}
            callApi={props.callRankingApi}
          />
          <Panel
            title="業種別騰落率"
            className="panel-industry-ratio"
            // refresh={() => {
            //   props.requestIndex({
            //     pathName: "Jpn.Industry.Main",
            //     setApiRes: props.setIndustryData,
            //   })
            // }}
          >
            <div className="list-block">
              <div className="control-row filter-control-row sp">
                <div className="title-border">値上がり率</div>
              </div>
              <div className="result-list-block">
                {isEmpty(industryData) ? (
                  <EmptyTable
                    className="table-list-view table-liquid-cell"
                    list={tableColumns.index.industry.industrySummary(
                      "値上がり率"
                    )}
                  />
                ) : (
                  <BaseTable
                    className="table-list-view table-liquid-cell"
                    list={tableColumns.index.industry.industrySummary(
                      "値上がり率"
                    )}
                    data={industryPriceIncreaseRate}
                    onClick={(rowData: object) => {
                      props.history.push({
                        pathname: "/domestic/industry",
                        state: { rowData },
                      })
                    }}
                  />
                )}
              </div>
            </div>
            <div className="list-block">
              <div className="control-row filter-control-row sp">
                <div className="title-border">値下がり率</div>
              </div>
              <div className="result-list-block">
                {isEmpty(industryData) ? (
                  <EmptyTable
                    className="table-list-view table-liquid-cell"
                    list={tableColumns.index.industry.industrySummary(
                      "値下がり率"
                    )}
                  />
                ) : (
                  <BaseTable
                    className="table-list-view table-liquid-cell"
                    list={tableColumns.index.industry.industrySummary(
                      "値下がり率"
                    )}
                    data={industryPriceDropRate}
                    onClick={(rowData: object) => {
                      props.history.push({
                        pathname: "/domestic/industry",
                        state: { rowData },
                      })
                    }}
                  />
                )}
              </div>
            </div>
          </Panel>
        </div>
      </div>
      <div className="contents-block">
        <div className="contents-nest-block">
          <NewsTopicsPanel />
          <DomesticSummaryIPO
            IpoData={props.ipoData}
            reload={() => {
              props.requestIpo({ setData: props.setIpoData })
            }}
            history={props.history}
          />
          <Panel
            title="国内経済指標"
            className="panel-economic-indicator panel-fixed-height"
            // refresh={() => {
            //   props.requestIndicatorList({
            //     setData: props.indicatorItem.changeData,
            //   })
            // }}
            filter={
              <>
                <div className="control-row panel-header-filter sp">
                  <BaseSelect
                    option={Sort.EconomicForeignDate}
                    value={props.indicatorItem.selectDay}
                    onChange={(num: number) => {
                      props.indicatorItem.changeDay(num)
                      props.indicatorItem.changePage(0)
                    }}
                  />
                </div>
                {/*<span className="panel-header-title-items time">*/}
                {/*  <div className="time-col liquid-item">*/}
                {/*    {dayjs().format("MM/DD HH:mm")}*/}
                {/*  </div>*/}
                {/*</span>*/}
              </>
            }
          >
            <div className="result-list-block">
              {!isPhone ? (
                <>
                  <BaseTable
                    className="table-list-view table-liquid-cell table-economic-indicator"
                    list={tableColumns.economics.index}
                    data={chunkIndicatorData[props.indicatorItem.currentPage]}
                  />
                  <Pagination
                    list={props.indicatorItem.data}
                    current={props.indicatorItem.currentPage}
                    page={PAGE_NUM.PAGE_10}
                    changePage={props.indicatorItem.changePage}
                  />
                </>
              ) : (
                <SpDetailTable
                  headerList={tableColumns.economics.sp.header}
                  bodyList={tableColumns.economics.sp.body}
                  data={props.indicatorItem.data}
                />
              )}
            </div>
          </Panel>
        </div>
      </div>
    </MainContainer>
  )
}
